import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChatCircleDots, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const ChatSettingsPage = () => {
  const [snippet, setSnippet] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/s/mrg`)
      .then(({ data }) => {
        setSnippet(data?.settings?.chat_widget_snippet || '');
        setEnabled(data?.settings?.chat_widget_enabled !== false);
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/store/settings`, {
        chat_widget_snippet: snippet,
        chat_widget_enabled: enabled,
      }, { withCredentials: true });
      toast.success('Widget chat enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[900px] mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ChatCircleDots size={26} weight="duotone" className="text-[#F9055B]" />Chat du site</h1>
        <p className="text-sm text-slate-500 mt-1">Activez un widget de chat client (Crisp, Tawk.to, Intercom, etc.) en collant simplement son code d&apos;intégration.</p>
      </header>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div>
            <p className="font-semibold">Widget actif</p>
            <p className="text-[12px] text-slate-500">Désactivez pour cacher temporairement le chat.</p>
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" checked={enabled} onChange={e => setEnabled(e.target.checked)} data-testid="chat-enabled-toggle" />
            <span className="w-11 h-6 bg-slate-200 peer-checked:bg-[#F9055B] rounded-full relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:w-5 after:h-5 after:rounded-full peer-checked:after:translate-x-5 after:transition-transform" />
          </label>
        </div>

        <div>
          <Label className="text-[13px] font-semibold mb-2 block">Code d&apos;intégration HTML / JavaScript</Label>
          <textarea
            value={snippet}
            onChange={e => setSnippet(e.target.value)}
            placeholder={`<!-- Exemple Crisp -->\n<script type="text/javascript">window.$crisp=[];window.CRISP_WEBSITE_ID="XXXXXXXX";(function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();</script>`}
            rows={10}
            className="w-full p-3 font-mono text-[12px] bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
            data-testid="chat-snippet-textarea"
            disabled={loading}
          />
          <p className="text-[11.5px] text-slate-500 mt-2">
            👉 Pour <a href="https://crisp.chat" target="_blank" rel="noreferrer" className="underline">Crisp</a> : Settings → Setup &amp; integrations → Website. Copiez l&apos;intégralité du snippet et collez-le ci-dessus.<br />
            👉 Pour <a href="https://www.tawk.to" target="_blank" rel="noreferrer" className="underline">Tawk.to</a> : Admin → Channels → Chat Widget. Copiez l&apos;intégralité du snippet.<br />
            ⚠️ Seuls les widgets via &lt;script&gt; sont supportés (pas d&apos;iframe direct).
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="bg-[#F9055B] hover:bg-[#d1004c]" data-testid="chat-save">
            <FloppyDisk size={15} className="mr-1.5" />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatSettingsPage;
